import { useState, useEffect, useCallback } from "react";
import { SharePointService } from "../services/sharePointService";
import { SharePointFile } from "../interfaces/sharePointTypes";
import { useAuth } from "../contexts/AuthContext";
import { createLogger } from "../utils/logger";

const log = createLogger("useSharePoint");

// Cache for file list operations (5-minute TTL)
// NOTE: This is a module-level cache shared across all hook instances.
// This improves performance when multiple components (e.g., ModernChatInput and
// DocumentLibraryModal) request the same data, preventing duplicate API calls.
//
// IMPORTANT: Any operation that mutates SharePoint data MUST clear the cache:
// - uploadFile() ✅ clears cache (line 165)
// - deleteFile() ✅ clears cache (line 270)
// - Future methods (updateFile, replaceFile, etc.) must also clear cache
interface CachedResult {
  data: SharePointFile[];
  timestamp: number;
}

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_ENTRIES = 50; // Maximum number of cached queries (LRU eviction)
const cache = new Map<string, CachedResult>();

/**
 * Helper to generate cache keys
 */
const getCacheKey = (search?: string, filesOnly?: boolean): string => {
  return `list:${search || "all"}:${filesOnly ? "files" : "all"}`;
};

/**
 * Helper to set cache entry with LRU eviction.
 * Evicts oldest entry when cache size exceeds MAX_CACHE_ENTRIES.
 */
const setCacheEntry = (
  key: string,
  value: CachedResult,
  logger: ReturnType<typeof createLogger>
): void => {
  try {
    cache.set(key, value);

    // Implement LRU eviction if cache exceeds max size
    if (cache.size > MAX_CACHE_ENTRIES) {
      // Map keys maintain insertion order - first key is oldest
      const oldestKey = cache.keys().next().value;
      if (oldestKey) {
        cache.delete(oldestKey);
        logger.info("Cache LRU eviction", {
          evictedKey: oldestKey,
          cacheSize: cache.size,
          maxSize: MAX_CACHE_ENTRIES,
        });
      }
    }

    logger.info("Cached file list", {
      cacheKey: key,
      count: value.data.length,
      cacheSize: cache.size,
    });
  } catch (cacheError) {
    logger.warn("Failed to cache file list", {
      cacheKey: key,
      error: cacheError instanceof Error ? cacheError.message : String(cacheError),
    });
    // Continue - cache failure is non-critical
  }
};

/**
 * React hook for SharePoint file operations
 * Provides methods to list, search, upload, and delete files from SharePoint
 */
export const useSharePoint = () => {
  const { accessToken } = useAuth();
  const [service, setService] = useState<SharePointService | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize SharePoint service when access token is available
  useEffect(() => {
    if (accessToken) {
      log.info("Initializing SharePoint service");
      setService(new SharePointService(accessToken));
    } else {
      log.warn("Access token not available, SharePoint service not initialized");
      setService(null);
    }
  }, [accessToken]);

  /**
   * List all files in the SharePoint folder
   * @param search - Optional search query to filter by filename
   * @param filesOnly - Filter out folders (default: true)
   * @returns Array of SharePointFile objects
   */
  const listFiles = useCallback(
    async (search?: string, filesOnly: boolean = true): Promise<SharePointFile[]> => {
      if (!service) {
        const error = "SharePoint service not initialized";
        log.error(error);
        throw new Error(error);
      }

      // Check cache first
      const cacheKey = getCacheKey(search, filesOnly);
      try {
        const cached = cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
          const age = Math.round((Date.now() - cached.timestamp) / 1000);
          log.info("Returning cached file list", {
            cacheKey,
            age: `${age}s`,
            count: cached.data.length,
          });
          return cached.data;
        }
      } catch (cacheError) {
        log.warn("Cache read failed, falling back to API", {
          cacheKey,
          error: cacheError instanceof Error ? cacheError.message : String(cacheError),
        });
        // Continue to API call below
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await service.listFiles({ search });
        let files = response.value;

        // Filter out folders if requested
        if (filesOnly) {
          files = files.filter((file) => !file.isFolder);
          log.info("Filtered files only", {
            originalCount: response.value.length,
            filteredCount: files.length,
          });
        }

        // Store in cache with LRU eviction
        setCacheEntry(cacheKey, { data: files, timestamp: Date.now() }, log);

        return files;
      } catch (err: any) {
        const errorMessage = err.message || "Failed to list files";
        log.error("Error listing files", { error: errorMessage });
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  /**
   * Search files by query
   * @param query - Search query string
   * @param limit - Maximum number of results to return
   * @returns Array of SharePointFile objects matching the query
   */
  const searchFiles = useCallback(
    async (query: string, limit?: number): Promise<SharePointFile[]> => {
      log.info("Searching files", { query, limit });
      const results = await listFiles(query);
      return limit ? results.slice(0, limit) : results;
    },
    [listFiles]
  );

  /**
   * Get recently modified files
   * @param limit - Maximum number of files to return (default: 5)
   * @returns Array of SharePointFile objects sorted by modified date descending
   */
  const getRecentFiles = useCallback(
    async (limit: number = 5): Promise<SharePointFile[]> => {
      log.info("Getting recent files", { limit });
      const results = await listFiles();

      // Sort by modified date descending (most recent first)
      const sorted = results.sort((a, b) => {
        const dateA = a.modifiedAt ?? a.createdAt;
        const dateB = b.modifiedAt ?? b.createdAt;
        return dateB.getTime() - dateA.getTime();
      });

      return sorted.slice(0, limit);
    },
    [listFiles]
  );

  /**
   * Upload a file to SharePoint
   * @param file - File object to upload
   * @returns Uploaded SharePointFile object
   */
  const uploadFile = useCallback(
    async (file: File): Promise<SharePointFile> => {
      if (!service) {
        const error = "SharePoint service not initialized";
        log.error(error);
        throw new Error(error);
      }

      setIsLoading(true);
      setError(null);

      try {
        log.info("Uploading file", { fileName: file.name, fileSize: file.size });
        const uploadedFile = await service.uploadFile(file);

        // Invalidate cache after upload
        try {
          cache.clear();
          log.info("SharePoint file cache cleared after upload");
        } catch (cacheError) {
          log.warn("Failed to clear cache after upload", {
            error: cacheError instanceof Error ? cacheError.message : String(cacheError),
          });
          // Continue - cache failure is non-critical
        }

        return uploadedFile;
      } catch (err: any) {
        const errorMessage = err.message || "Failed to upload file";
        log.error("Error uploading file", { error: errorMessage });
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  /**
   * Download file content from SharePoint
   * @param fileId - ID of the file to download
   * @returns Blob containing the file content
   */
  const downloadFile = useCallback(
    async (fileId: string): Promise<Blob> => {
      if (!service) {
        const error = "SharePoint service not initialized";
        log.error(error);
        throw new Error(error);
      }

      setIsLoading(true);
      setError(null);

      try {
        log.info("Downloading file", { fileId });
        const blob = await service.downloadFile(fileId);
        return blob;
      } catch (err: any) {
        const errorMessage = err.message || "Failed to download file";
        log.error("Error downloading file", { error: errorMessage });
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  /**
   * Download SharePoint file as File object with metadata.
   * Manages loading state and error handling automatically.
   *
   * @param file - SharePointFile metadata object
   * @returns File object ready for use in UI/APIs
   */
  const downloadFileAsFile = useCallback(
    async (file: SharePointFile): Promise<File> => {
      if (!service) {
        const error = "SharePoint service not initialized";
        log.error(error);
        throw new Error(error);
      }

      setIsLoading(true);
      setError(null);

      try {
        const fileObject = await service.downloadFileAsFile(file);
        log.info("Successfully downloaded file via hook", {
          fileName: file.name,
          fileSize: fileObject.size,
        });
        return fileObject;
      } catch (err: any) {
        const errorMessage = err.message || "Failed to download file";
        log.error("Error downloading file as File object", { error: errorMessage });
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  /**
   * Delete a file from SharePoint
   * @param fileId - ID of the file to delete
   */
  const deleteFile = useCallback(
    async (fileId: string): Promise<void> => {
      if (!service) {
        const error = "SharePoint service not initialized";
        log.error(error);
        throw new Error(error);
      }

      setIsLoading(true);
      setError(null);

      try {
        log.info("Deleting file", { fileId });
        await service.deleteFile(fileId);

        // Invalidate cache after delete
        try {
          cache.clear();
          log.info("SharePoint file cache cleared after delete");
        } catch (cacheError) {
          log.warn("Failed to clear cache after delete", {
            error: cacheError instanceof Error ? cacheError.message : String(cacheError),
          });
          // Continue - cache failure is non-critical
        }
      } catch (err: any) {
        const errorMessage = err.message || "Failed to delete file";
        log.error("Error deleting file", { error: errorMessage });
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  /**
   * Clear the file list cache.
   * Useful for forcing a refresh of file lists.
   */
  const clearCache = useCallback(() => {
    try {
      cache.clear();
      log.info("SharePoint file cache manually cleared");
    } catch (error) {
      log.warn("Failed to clear cache manually", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  return {
    isLoading,
    error,
    isInitialized: !!service,
    listFiles,
    searchFiles,
    getRecentFiles,
    uploadFile,
    downloadFile,
    downloadFileAsFile,
    deleteFile,
    clearCache,
  };
};
